import { useEffect, useRef, useState } from 'react';

import { AdminIcon } from '@/components/admin/icon';
import { PageHead } from '@/components/admin/page-head';
import { useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import {
  importExportApi,
  type ImportJobStatus,
  type ImportPreview,
} from '@/lib/catalog/catalog-api';

export function AdminImportExportPage() {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState<'idle' | 'preview' | 'commit' | 'export' | 'template'>('idle');
  const [job, setJob] = useState<ImportJobStatus | null>(null);
  const [dragging, setDragging] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dropInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runPreview = async (f: File) => {
    setBusy('preview');
    setPreview(null);
    setJob(null);
    try {
      setPreview(await importExportApi.preview(f));
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Analyse impossible'));
    } finally {
      setBusy('idle');
    }
  };

  const onPick = (f: File | null) => {
    setFile(f);
    if (f) void runPreview(f);
    else setPreview(null);
  };

  const commit = async () => {
    if (!file) return;
    setBusy('commit');
    try {
      const { jobId } = await importExportApi.commit(file);
      pollRef.current = setInterval(async () => {
        try {
          const j = await importExportApi.job(jobId);
          setJob(j);
          if (j.status === 'completed' || j.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            if (j.status === 'completed')
              toast.success(`Import terminé : ${j.created} créés, ${j.updated} mis à jour.`);
          }
        } catch {
          /* poll error ignoré */
        }
      }, 800);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Import impossible'));
    } finally {
      setBusy('idle');
    }
  };

  const doDownload = async (kind: 'template' | 'export') => {
    setBusy(kind);
    try {
      if (kind === 'template') await importExportApi.downloadTemplate();
      else await importExportApi.downloadExport();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Téléchargement impossible'));
    } finally {
      setBusy('idle');
    }
  };

  const validCount = preview ? preview.summary.toCreate + preview.summary.toUpdate : 0;
  const pct = job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <>
      <PageHead
        title="Import / Export"
        sub="Créez et mettez à jour des produits en masse depuis un fichier Excel — sans développeur."
      />

      {/* Export */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h"><h3>Export</h3></div>
        <div className="card-b" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={() => void doDownload('export')} disabled={busy === 'export'}>
            {busy === 'export' ? 'Préparation…' : 'Exporter tous les produits (XLSX)'}
          </button>
          <span className="muted" style={{ fontSize: 13 }}>Fonctionne au-delà de 1000 produits.</span>
        </div>
      </div>

      {/* Import */}
      <div className="card">
        <div className="card-h"><h3>Import</h3></div>
        <div className="card-b">
          {/* Étape 1 — modèle + explication */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              padding: '12px 14px',
              marginBottom: 14,
              background: 'var(--a-surface-2)',
              border: '1px solid var(--a-border,#eee)',
              borderRadius: 'var(--a-r-sm, 8px)',
            }}
          >
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>1. Partez du modèle Excel</div>
              <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                Colonnes : <code>sku</code>, <code>title</code>, <code>category_code</code>,{' '}
                <code>brand</code>, <code>base_price</code>, <code>status</code>… Un <code>sku</code>{' '}
                existant <strong>met à jour</strong> le produit, un nouveau le <strong>crée</strong>.
                L'aperçu signale les erreurs <strong>avant</strong> toute écriture.
              </p>
            </div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void doDownload('template')}
              disabled={busy === 'template'}
              style={{ flexShrink: 0 }}
            >
              <AdminIcon n="download" s={14} />{' '}
              {busy === 'template' ? 'Préparation…' : 'Télécharger le modèle (.xlsx)'}
            </button>
          </div>

          {/* Étape 2 — dépôt du fichier (glisser-déposer) */}
          <div style={{ fontWeight: 600, marginBottom: 6 }}>2. Déposez votre fichier rempli</div>
          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onPick(f);
            }}
            onClick={() => dropInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dropInputRef.current?.click(); }}
            style={{
              border: `2px dashed ${dragging ? 'var(--a-primary,#2563eb)' : 'var(--a-border-strong,#cbd5e1)'}`,
              background: dragging ? 'var(--a-primary-soft, rgba(37,99,235,0.06))' : 'var(--a-surface-2)',
              borderRadius: 'var(--a-r-sm, 8px)',
              padding: '26px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color .15s, background .15s',
            }}
          >
            <AdminIcon n="upDown" s={26} />
            <div style={{ marginTop: 8, fontWeight: 500 }}>
              {file ? file.name : 'Glissez-déposez votre fichier ici'}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {file ? 'Cliquez pour changer de fichier' : 'ou cliquez pour parcourir — .xlsx / .xls'}
            </div>
            <input
              ref={dropInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => onPick(e.target.files?.[0] ?? null)}
            />
          </div>
          {file && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="tag success no-dot" style={{ fontSize: 11 }}>
                Fichier sélectionné : {file.name}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => onPick(null)}>
                Retirer
              </button>
            </div>
          )}

          {busy === 'preview' && <p className="muted" style={{ marginTop: 12 }}>Analyse du fichier…</p>}

          {preview && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span className="tag neutral no-dot">{preview.summary.total} lignes</span>
                <span className="tag success no-dot">{preview.summary.toCreate} à créer</span>
                <span className="tag info no-dot">{preview.summary.toUpdate} à mettre à jour</span>
                {preview.summary.invalid > 0 && (
                  <span className="tag warning no-dot">{preview.summary.invalid} en erreur</span>
                )}
              </div>

              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--a-border,#eee)', borderRadius: 6, marginBottom: 12 }}>
                <table className="table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Ligne</th>
                      <th style={{ width: 90 }}>Action</th>
                      <th>SKU</th>
                      <th>Constat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.line} style={{ background: r.errors.length ? 'rgba(200,0,0,0.05)' : undefined }}>
                        <td>{r.line}</td>
                        <td>{r.action === 'create' ? 'Création' : 'MàJ'}</td>
                        <td className="mono" style={{ fontSize: 12 }}>{r.data.sku || '—'}</td>
                        <td style={{ fontSize: 12 }}>
                          {r.errors.length ? (
                            <span style={{ color: 'var(--a-danger,#c00)' }}>{r.errors.join(' · ')}</span>
                          ) : (
                            <span style={{ color: 'var(--a-success,#0a7)' }}>Valide</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                className="btn btn-primary"
                onClick={() => void commit()}
                disabled={validCount === 0 || busy === 'commit' || (!!job && job.status === 'processing')}
              >
                {busy === 'commit' ? 'Lancement…' : `3. Valider l'import (${validCount} ligne(s) valide(s))`}
              </button>
              {preview.summary.invalid > 0 && (
                <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>
                  Les {preview.summary.invalid} ligne(s) en erreur seront ignorées.
                </span>
              )}
            </>
          )}

          {job && (
            <div style={{ marginTop: 16, padding: 12, border: '1px solid var(--a-border,#eee)', borderRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <strong>
                  {job.status === 'completed' ? 'Import terminé' : job.status === 'failed' ? 'Import échoué' : 'Import en cours…'}
                </strong>
                <span>{job.processed}/{job.total} ({pct}%)</span>
              </div>
              <div style={{ height: 8, background: 'var(--a-border,#eee)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: job.status === 'failed' ? 'var(--a-danger,#c00)' : 'var(--a-success,#0a7)', transition: 'width .3s' }} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12 }} className="muted">
                {job.created} créés · {job.updated} mis à jour · {job.failed} en erreur
              </div>
              {job.errors.length > 0 && (
                <ul style={{ marginTop: 8, fontSize: 12, color: 'var(--a-danger,#c00)', paddingLeft: 18 }}>
                  {job.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Ligne {e.line} : {e.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
